import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: '糖心传媒',
        url: 'https://shoudujiyin.com.cn/',
      },
      publisher: {
        '@type': 'Organization',
        name: '糖心传媒',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://shoudujiyin.com.cn/article/${article.slug}`,
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://shoudujiyin.com.cn/' },
        { '@type': 'ListItem', position: 2, name: '案例洞察', item: 'https://shoudujiyin.com.cn/category/all' },
        { '@type': 'ListItem', position: 3, name: article.categoryName, item: `https://shoudujiyin.com.cn/category/${article.category}` },
        { '@type': 'ListItem', position: 4, name: article.title },
      ],
    };

    const el1 = document.createElement('script');
    el1.type = 'application/ld+json';
    el1.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(el1);

    const el2 = document.createElement('script');
    el2.type = 'application/ld+json';
    el2.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(el2);

    return () => {
      document.head.removeChild(el1);
      document.head.removeChild(el2);
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/category/all" className="text-primary-500 hover:text-primary-600 font-medium">
              返回案例洞察
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 2);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <article>
          <section className="pt-24 md:pt-28 pb-0 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '案例洞察', to: '/category/all' },
                  { label: article.categoryName, to: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-8 mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读 {article.readTime}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                  {article.excerpt}
                </p>
              </div>
            </div>
          </section>

          <section className="py-6 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="aspect-[16/9] rounded-2xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="article-content">
                {article.content.split('\n').map((paragraph, idx) => {
                  const trimmed = paragraph.trim();
                  if (!trimmed) return null;

                  if (trimmed.startsWith('## ')) {
                    return (
                      <h2 key={idx} id={`section-${idx}`}>
                        {trimmed.replace('## ', '')}
                      </h2>
                    );
                  }

                  if (trimmed.startsWith('- ')) {
                    return (
                      <li key={idx} className="ml-4 list-disc">
                        {trimmed.replace('- ', '')}
                      </li>
                    );
                }

                  return <p key={idx}>{trimmed}</p>;
                })}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200/60">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-background-100 text-foreground-600 px-3 py-1.5 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">相关推荐</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col sm:flex-row"
                  >
                    <div className="sm:w-48 shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5 flex-1">
                      <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full font-medium">
                        {related.categoryName}
                      </span>
                      <h4 className="text-base font-bold text-foreground-950 mt-2 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{related.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 md:py-16 bg-background-50 text-center">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span>返回案例洞察</span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}